import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CssBox, mergeLineBoxes, PdfPageView, toCssBox, toNormalisedRect } from './pdf-geometry';
import { documentOptions, loadPdfJs, PdfJsLib, PdfJsLoadingTask, PdfJsPage, PdfJsTextLayer } from './pdfjs-loader';
import { PdfAnnotationDraft, PdfHighlight, PdfHighlightAuthor, PdfMarkMode } from './pdf-viewer.types';
import { anchorOnPage, nextGestureScale, PageAnchor, scrollToKeepAnchor } from './pdf-zoom';

//REV-21369 — der Beleg-Viewer (ADR-0021). Contract: e2e-lab/pdf-viewer.lab.spec.ts.
//
//PRÄSENTATIONAL MIT RENDERING-SEITENEFFEKT (E6): kein Store, kein `HttpClient`. Die Bytes kommen als
//`@Input()`, Markierungen gehen als `@Output()` hinaus. Canvas, Worker und Textschicht sind Zeichnen
//in die eigene Fläche — das gehört in die Komponente; Netz und Speichern gehören dem Wirt.
//
//⚠️ `ViewEncapsulation.None` (Spike-Befund 3): pdf.js erzeugt Textschicht-Knoten zur Laufzeit, ohne
//`_ngcontent-*`-Attribut; emuliertes Komponenten-CSS träfe sie nicht. Ersatz für die Kapselung ist
//das Präfix: JEDER Selektor in `pdf-viewer.component.scss` beginnt mit `abcp-pdf`.
//
//⚠️ Fremd-PDFs (E7): keine AnnotationLayer (Links, Formulare des PDF-Erstellers), kein Scripting.

/** Wie weit vor dem Sichtbereich schon gezeichnet wird — eine Seite Vorlauf in jede Richtung. */
const RENDER_MARGIN = '100% 0px';
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
/** Eine aufgezogene Fläche unter dieser Kantenlänge (px) war ein Klick, keine Markierung. */
const MIN_AREA_PX = 6;
/** Wie der Server (DTO): ein Zitat ist ein Hinweis auf die Stelle, keine Kopie des Textes. */
const MAX_SNIPPET = 500;
/** No zoom event for this long ends the gesture, and the pages are drawn sharp. */
const ZOOM_IDLE_MS = 150;

/** Ein Aufräumschritt, der nie wirft — siehe `teardown`. */
function safely(step: () => unknown): void {
    try {
        step();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[abcp-pdf-viewer] cleanup step failed', error);
    }
}

/** Zerstört einen Lade-Task, ohne dass ein Fehler (synchron oder als Promise) nach aussen dringt. */
function destroyQuietly(task: PdfJsLoadingTask): void {
    safely(() => {
        void task.destroy().catch(() => undefined);
    });
}

interface PageShell {
    number: number;
    /** Masse bei Massstab 1, gedreht — die Grundlage jeder Zoom-Stufe. */
    baseWidth: number;
    baseHeight: number;
    view: PdfPageView;
    rotation: number;
}

interface PageMarks {
    id: string;
    author: PdfHighlightAuthor;
    boxes: CssBox[];
}

interface PendingDraft extends PdfAnnotationDraft {
    /** Wo der Knopf „Markieren & kommentieren" steht — relativ zur Seite. */
    actionLeft: number;
    actionTop: number;
}

@Component({
    selector: 'abcp-pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class AbcpPdfViewerComponent implements OnChanges, OnDestroy {
    /** Die Originalbytes (ADR-0021 E3). Der Viewer holt NIE selbst. */
    @Input() data: ArrayBuffer | null = null;
    @Input() fileName = '';
    @Input() loading = false;
    @Input() error = false;
    @Input() highlights: PdfHighlight[] = [];
    /** Die hervorgehobene Markierung (ausgewählt oder per `?note=` angesprungen). */
    @Input() activeHighlightId: string | null = null;
    /** Ohne Recht keine Markier-Bedienung — entschieden vom Wirt, nie hier. */
    @Input() canAnnotate = false;
    /**
     * REV-22352 — die Markierung, zu der gerade ein Kommentar geschrieben wird. Bleibt sichtbar
     * (blau, laufender gepunkteter Rand, wie schon der Entwurf davor), bis der Wirt sie zurücknimmt:
     * gespeichert, abgebrochen oder das Zitat-Kästchen abgewählt.
     */
    @Input() pendingMark: PdfAnnotationDraft | null = null;
    @Input() canDownload = true;
    /**
     * Offer «Pendenz erfassen» next to «Kommentieren» on a drafted mark. The account page
     * turns it on; the document page keeps its single action.
     */
    @Input() todoAction = false;

    @Output() annotationDrafted = new EventEmitter<PdfAnnotationDraft>();
    /** The drafted mark as the start of a Pendenz. */
    @Output() todoDrafted = new EventEmitter<PdfAnnotationDraft>();

    /** The second action sits right below the first (toolbar button height plus a gap). */
    public readonly todoActionOffset = 36;
    @Output() highlightSelected = new EventEmitter<string>();
    @Output() downloadRequested = new EventEmitter<void>();
    @Output() pageChanged = new EventEmitter<number>();

    @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

    public status: 'idle' | 'opening' | 'ready' | 'failed' = 'idle';
    public pages: PageShell[] = [];
    public scale = 1;
    public mode: PdfMarkMode = 'text';
    public currentPage = 1;
    private draftValue: PendingDraft | null = null;
    public rubber: { page: number; box: CssBox } | null = null;
    public marksByPage = new Map<number, PageMarks[]>();
    public pendingBoxes: { page: number; boxes: CssBox[] } | null = null;
    /**
     * A pinch or Ctrl + wheel is running: the pages follow at once with their current pictures
     * stretched, and are drawn sharp only when the gesture stops.
     */
    public zooming = false;

    /**
     * The marking just made, before "Markieren & kommentieren". REV-22352: it already looks like the
     * marking being commented (`pendingMark`), so nothing changes colour on the click.
     */
    public get draft(): PendingDraft | null {
        return this.draftValue;
    }

    public set draft(value: PendingDraft | null) {
        this.draftValue = value;
        this.pendingBoxes = this.boxesOf(value ?? this.pendingMark);
    }

    private lib: PdfJsLib | null = null;
    /** Der Lade-Task der offenen Datei — DAS ist in pdf.js 6 das Zerstörbare, nicht das Dokument. */
    private task: PdfJsLoadingTask | null = null;
    private readonly pdfPages = new Map<number, PdfJsPage>();
    private readonly rendered = new Set<number>();
    private readonly renderTasks = new Map<number, { cancel(): void }>();
    private readonly textLayers = new Map<number, PdfJsTextLayer>();
    private observer: IntersectionObserver | null = null;
    /** Jede neue Datei erhöht ihn; eine Antwort zu einer älteren wird verworfen. */
    private generation = 0;
    private scrollFrame: number | null = null;
    /** The latest zoom of the running gesture, applied once per animation frame. */
    private zoomTarget: { scale: number; clientX: number; clientY: number } | null = null;
    private zoomFrame: number | null = null;
    private zoomIdleTimer: ReturnType<typeof setTimeout> | null = null;

    public constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {
        // Not a (wheel) binding: only a listener with `passive: false` may stop the browser zooming the
        // whole page, and outside Angular every wheel event does not start a change detection.
        this.zone.runOutsideAngular(() => host.nativeElement.addEventListener('wheel', this.onWheel, { passive: false }));
    }

    // ── Zustand für die Vorlage ───────────────────────────────────────────────────────────────

    /** Präzedenz wie in der Vorschau: lädt > Fehler > Dokument. */
    public get viewState(): 'loading' | 'error' | 'empty' | 'opening' | 'ready' {
        if (this.loading) {
            return 'loading';
        }
        if (this.error || this.status === 'failed') {
            return 'error';
        }
        if (this.data === null) {
            return 'empty';
        }
        return this.status === 'ready' ? 'ready' : 'opening';
    }

    public get zoomPercent(): number {
        return Math.round(this.scale * 100);
    }

    public trackPage = (_: number, page: PageShell): number => page.number;
    public trackMark = (_: number, mark: PageMarks): string => mark.id;

    public marksOf(page: number): PageMarks[] {
        return this.marksByPage.get(page) ?? [];
    }

    // ── Lebenszyklus ──────────────────────────────────────────────────────────────────────────

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            void this.open(this.data);
        }
        if (changes['highlights'] || changes['pendingMark']) {
            this.recomputeMarks();
        }
        if (changes['activeHighlightId'] && this.activeHighlightId !== null) {
            this.revealActive();
        }
        if (changes['canAnnotate'] && !this.canAnnotate) {
            this.clearDraft();
        }
        // Lade- und Fehlerzustand verdecken die Seiten (sie werden dann nicht gezeichnet). Wird der
        // Viewer wieder „bereit", gibt es neue Seitenknoten, und die müssen beobachtet werden.
        if ((changes['loading'] || changes['error']) && this.viewState === 'ready') {
            this.rendered.clear();
            setTimeout(() => this.observePages());
        }
    }

    public ngOnDestroy(): void {
        this.host.nativeElement.removeEventListener('wheel', this.onWheel);
        if (this.zoomFrame !== null) {
            cancelAnimationFrame(this.zoomFrame);
        }
        if (this.zoomIdleTimer !== null) {
            clearTimeout(this.zoomIdleTimer);
        }
        this.generation++;
        this.teardown();
    }

    // ── Bedienung ─────────────────────────────────────────────────────────────────────────────

    public previousPage(): void {
        this.goToPage(this.currentPage - 1);
    }

    public nextPage(): void {
        this.goToPage(this.currentPage + 1);
    }

    public zoomIn(): void {
        const next = ZOOM_STEPS.find((s) => s > this.scale + 0.001);
        if (next !== undefined) {
            this.setScale(next);
        }
    }

    public zoomOut(): void {
        const next = [...ZOOM_STEPS].reverse().find((s) => s < this.scale - 0.001);
        if (next !== undefined) {
            this.setScale(next);
        }
    }

    /** Seitenbreite: die breiteste Seite füllt den Sichtbereich. */
    public fitWidth(): void {
        const el = this.scroller?.nativeElement;
        const widest = Math.max(...this.pages.map((p) => p.baseWidth), 1);
        if (!el || el.clientWidth === 0) {
            return;
        }
        // 32 px: der Innenabstand des Scrollbereichs links und rechts.
        this.setScale(Math.max(0.25, Math.min(4, (el.clientWidth - 32) / widest)));
    }

    public setMode(mode: PdfMarkMode): void {
        this.mode = mode;
        this.clearDraft();
    }

    public confirmDraft(): void {
        if (this.draft === null) {
            return;
        }
        const { page, rects, snippet } = this.draft;
        this.annotationDrafted.emit({ page, rects, snippet });
        this.clearDraft();
    }

    public confirmTodoDraft(): void {
        if (this.draft === null) {
            return;
        }
        const { page, rects, snippet } = this.draft;
        this.todoDrafted.emit({ page, rects, snippet });
        this.clearDraft();
    }

    @HostListener('keydown.escape')
    public clearDraft(): void {
        if (this.draft !== null || this.rubber !== null) {
            this.draft = null;
            this.rubber = null;
            window.getSelection()?.removeAllRanges();
            this.cdr.markForCheck();
        }
    }

    public selectHighlight(id: string, event: MouseEvent): void {
        event.stopPropagation();
        this.highlightSelected.emit(id);
    }

    /** Text-Modus: aus der Auswahl in EINER Seite wird ein Entwurf. */
    public onPointerUp(event: MouseEvent): void {
        if (!this.canAnnotate || this.mode !== 'text') {
            return;
        }
        if (event.target instanceof Element && event.target.closest('[data-testid="pdf-viewer-mark-action"]')) {
            return;
        }
        const selection = window.getSelection();
        const text = selection ? selection.toString().trim() : '';
        if (!selection || selection.rangeCount === 0 || text.length === 0) {
            if (this.draft !== null) {
                this.draft = null;
                this.cdr.markForCheck();
            }
            return;
        }
        const range = selection.getRangeAt(0);
        const startNode = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement;
        const pageEl = startNode?.closest<HTMLElement>('.abcp-pdf-page') ?? null;
        if (pageEl === null || !this.host.nativeElement.contains(pageEl)) {
            return;
        }
        const pageNumber = Number(pageEl.dataset['page']);
        const origin = pageEl.getBoundingClientRect();
        // ⚠️ Nur die Rechtecke DIESER Seite: eine Auswahl über zwei Seiten wird zu einer Markierung
        // auf der Seite, auf der sie beginnt — eine Notiz hat genau eine Fundstelle.
        const boxes = Array.from(range.getClientRects())
            .map((r) => ({ left: r.left - origin.left, top: r.top - origin.top, width: r.width, height: r.height }))
            .filter((b) => b.left < origin.width && b.top < origin.height && b.left + b.width > 0 && b.top + b.height > 0);
        this.setDraft(pageNumber, mergeLineBoxes(boxes), text.slice(0, MAX_SNIPPET));
    }

    /** Flächen-Modus: eine Fläche aufziehen — für Scans ohne Textschicht (ADR-0021 E5). */
    public onPagePointerDown(event: MouseEvent, page: PageShell): void {
        if (!this.canAnnotate || this.mode !== 'area' || event.button !== 0) {
            return;
        }
        event.preventDefault();
        const pageEl = event.currentTarget as HTMLElement;
        const origin = pageEl.getBoundingClientRect();
        const startX = event.clientX - origin.left;
        const startY = event.clientY - origin.top;
        this.draft = null;

        const move = (e: MouseEvent): void => {
            const x = Math.min(Math.max(e.clientX - origin.left, 0), origin.width);
            const y = Math.min(Math.max(e.clientY - origin.top, 0), origin.height);
            this.zone.run(() => {
                this.rubber = {
                    page: page.number,
                    box: { left: Math.min(startX, x), top: Math.min(startY, y), width: Math.abs(x - startX), height: Math.abs(y - startY) },
                };
                this.cdr.markForCheck();
            });
        };
        const up = (): void => {
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', up);
            this.zone.run(() => {
                const box = this.rubber?.box ?? null;
                this.rubber = null;
                if (box !== null && box.width >= MIN_AREA_PX && box.height >= MIN_AREA_PX) {
                    this.setDraft(page.number, [box], null);
                } else {
                    this.cdr.markForCheck();
                }
            });
        };
        this.zone.runOutsideAngular(() => {
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
        });
    }

    public onScroll(): void {
        if (this.scrollFrame !== null) {
            return;
        }
        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateCurrentPage();
        });
    }

    // ── Zoom with the gesture ─────────────────────────────────────────────────────────────────

    /** A touchpad pinch arrives as a wheel event with `ctrlKey`, like Ctrl + mouse wheel. */
    private readonly onWheel = (event: WheelEvent): void => {
        const scroller = this.scroller?.nativeElement;
        if (!event.ctrlKey || this.viewState !== 'ready' || !scroller || !(event.target instanceof Node) || !scroller.contains(event.target)) {
            return;
        }
        event.preventDefault();
        const from = this.zoomTarget?.scale ?? this.scale;
        this.zoomTarget = { scale: nextGestureScale(from, event.deltaY, event.deltaMode), clientX: event.clientX, clientY: event.clientY };
        if (this.zoomFrame === null) {
            this.zoomFrame = requestAnimationFrame(() => this.applyGestureZoom());
        }
        if (this.zoomIdleTimer !== null) {
            clearTimeout(this.zoomIdleTimer);
        }
        this.zoomIdleTimer = setTimeout(() => this.zone.run(() => this.endGestureZoom()), ZOOM_IDLE_MS);
    };

    /** Resize the pages to the new scale and scroll so the spot under the cursor stays put. */
    private applyGestureZoom(): void {
        this.zoomFrame = null;
        const target = this.zoomTarget;
        const scroller = this.scroller?.nativeElement;
        this.zoomTarget = null;
        if (target === null || !scroller) {
            return;
        }
        if (!this.zooming) {
            this.zooming = true;
            // Like the +/− buttons: a marking in progress is dropped, its button would sit in the wrong place.
            this.draft = null;
            this.rubber = null;
        }
        const anchor = this.anchorAt(target.clientX, target.clientY);
        this.scale = target.scale;
        this.recomputeMarks();
        this.cdr.detectChanges();
        const pageEl = anchor === null ? null : scroller.querySelector<HTMLElement>(`.abcp-pdf-page[data-page="${anchor.page}"]`);
        if (anchor === null || pageEl === null) {
            return;
        }
        const origin = scroller.getBoundingClientRect();
        const scroll = scrollToKeepAnchor(
            anchor,
            { left: pageEl.offsetLeft, top: pageEl.offsetTop, width: pageEl.offsetWidth, height: pageEl.offsetHeight },
            { x: target.clientX - origin.left - scroller.clientLeft, y: target.clientY - origin.top - scroller.clientTop },
        );
        scroller.scrollLeft = scroll.left;
        scroller.scrollTop = scroll.top;
    }

    /** The gesture stopped: draw the pages sharp at the scale they already have. */
    private endGestureZoom(): void {
        this.zoomIdleTimer = null;
        if (this.zoomFrame !== null) {
            cancelAnimationFrame(this.zoomFrame);
            this.applyGestureZoom();
        }
        this.zooming = false;
        this.setScale(this.scale);
    }

    /** The page under the cursor, or the nearest one when the cursor is in the gap between two. */
    private anchorAt(clientX: number, clientY: number): PageAnchor | null {
        const pages = Array.from(this.scroller?.nativeElement.querySelectorAll<HTMLElement>('.abcp-pdf-page') ?? []);
        let nearest: HTMLElement | null = null;
        let nearestDistance = Infinity;
        for (const el of pages) {
            const rect = el.getBoundingClientRect();
            const distance = clientY < rect.top ? rect.top - clientY : clientY > rect.bottom ? clientY - rect.bottom : 0;
            if (distance < nearestDistance) {
                nearest = el;
                nearestDistance = distance;
            }
        }
        if (nearest === null) {
            return null;
        }
        return anchorOnPage(Number(nearest.dataset['page']), nearest.getBoundingClientRect(), clientX, clientY);
    }

    // ── Öffnen und Zeichnen ───────────────────────────────────────────────────────────────────

    private async open(data: ArrayBuffer | null): Promise<void> {
        const generation = ++this.generation;
        this.teardown();
        this.pages = [];
        this.marksByPage = new Map();
        this.currentPage = 1;
        this.draft = null;
        if (data === null) {
            this.status = 'idle';
            this.cdr.markForCheck();
            return;
        }
        this.status = 'opening';
        this.cdr.markForCheck();

        try {
            const shells = await this.zone.runOutsideAngular(async () => {
                this.lib = await loadPdfJs();
                const task = this.lib.getDocument(documentOptions(data));
                this.task = task;
                const doc = await task.promise;
                if (generation !== this.generation) {
                    // Eine neuere Datei ist schon unterwegs — DIESEN Lade-Task (nicht das Dokument)
                    // zerstören. Das Dokument hat in pdf.js 6 kein `destroy()`.
                    destroyQuietly(task);
                    return null;
                }
                const result: PageShell[] = [];
                for (let n = 1; n <= doc.numPages; n++) {
                    const page = await doc.getPage(n);
                    this.pdfPages.set(n, page);
                    const viewport = page.getViewport({ scale: 1 });
                    result.push({
                        number: n,
                        baseWidth: viewport.width,
                        baseHeight: viewport.height,
                        view: page.view,
                        rotation: page.rotate,
                    });
                }
                return result;
            });
            if (shells === null || generation !== this.generation) {
                return;
            }
            this.pages = shells;
            this.status = 'ready';
            this.recomputeMarks();
            this.cdr.markForCheck();
            // Nach dem Zeichnen der Seitenrahmen: auf Breite einpassen, dann beobachten.
            setTimeout(() => {
                if (generation !== this.generation) {
                    return;
                }
                this.fitWidth();
                // Auch ohne Einpassen (Breite noch 0, z. B. verborgener Reiter) beobachten — sonst
                // zeichnete der Viewer nie eine Seite.
                this.observePages();
                if (this.activeHighlightId !== null) {
                    this.revealActive();
                }
            });
        } catch (error) {
            if (generation !== this.generation) {
                return;
            }
            // eslint-disable-next-line no-console
            console.error('[abcp-pdf-viewer] could not open the document', error);
            this.status = 'failed';
            this.cdr.markForCheck();
        }
    }

    private setScale(scale: number): void {
        this.scale = scale;
        this.clearRendered();
        this.recomputeMarks();
        this.draft = null;
        this.cdr.markForCheck();
        // Erst nach dem Neuzeichnen der Rahmen beobachten — sonst misst der Observer die alten.
        setTimeout(() => this.observePages());
    }

    private observePages(): void {
        this.observer?.disconnect();
        const root = this.scroller?.nativeElement;
        if (!root) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            this.observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            void this.renderPage(Number((entry.target as HTMLElement).dataset['page']));
                        }
                    }
                },
                { root, rootMargin: RENDER_MARGIN },
            );
            root.querySelectorAll<HTMLElement>('.abcp-pdf-page').forEach((el) => this.observer?.observe(el));
        });
    }

    private async renderPage(pageNumber: number): Promise<void> {
        const page = this.pdfPages.get(pageNumber);
        const lib = this.lib;
        const pageEl = this.scroller?.nativeElement.querySelector<HTMLElement>(`.abcp-pdf-page[data-page="${pageNumber}"]`);
        const target = pageEl?.querySelector<HTMLElement>('.abcp-pdf-page__render');
        // During a gesture the scale changes every frame; the pages are drawn once it stops.
        if (!page || !lib || !pageEl || !target || this.rendered.has(pageNumber) || this.zooming) {
            return;
        }
        this.rendered.add(pageNumber);
        const scale = this.scale;
        const viewport = page.getViewport({ scale });
        const ratio = window.devicePixelRatio || 1;

        // Die CSS-Variablen, die das Textschicht-CSS von pdf.js erwartet (Spike-Befund 6). Fehlt
        // `--scale-round-x/-y`, ist die Breite der Schicht ungültig, und auf gedrehten Seiten liegt
        // der Text neben dem gezeichneten.
        pageEl.style.setProperty('--scale-factor', String(scale));
        pageEl.style.setProperty('--total-scale-factor', String(scale));
        pageEl.style.setProperty('--scale-round-x', '1px');
        pageEl.style.setProperty('--scale-round-y', '1px');

        // Drawn off screen and swapped in when done: the previous picture (stretched after a zoom) stays
        // until then, so a zoom does not flash white. The size comes from the page box (CSS 100 %).
        const canvas = document.createElement('canvas');
        canvas.className = 'abcp-pdf-page__canvas';
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.setAttribute('aria-hidden', 'true');

        const task = page.render({ canvas, viewport, transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined });
        this.renderTasks.set(pageNumber, task);
        try {
            await task.promise;
        } catch {
            // Abgebrochen (Zoom, neue Datei) — der nächste Durchgang zeichnet neu.
            this.rendered.delete(pageNumber);
            return;
        } finally {
            this.renderTasks.delete(pageNumber);
        }
        if (scale !== this.scale) {
            return;
        }
        target.textContent = '';
        target.appendChild(canvas);

        const textEl = document.createElement('div');
        textEl.className = 'textLayer';
        target.appendChild(textEl);
        const textLayer = new lib.TextLayer({ textContentSource: await page.getTextContent(), container: textEl, viewport });
        this.textLayers.set(pageNumber, textLayer);
        try {
            await textLayer.render();
        } catch {
            // Eine fehlende Textschicht (z. B. abgebrochen) macht die Seite nicht unbrauchbar.
        }
        pageEl.dataset['rendered'] = 'true';
        pageEl.dataset['textItems'] = String(textEl.querySelectorAll('span').length);
    }

    private clearRendered(): void {
        this.renderTasks.forEach((task) => task.cancel());
        this.renderTasks.clear();
        this.textLayers.forEach((layer) => layer.cancel());
        this.textLayers.clear();
        this.rendered.clear();
        this.scroller?.nativeElement.querySelectorAll<HTMLElement>('.abcp-pdf-page').forEach((el) => {
            delete el.dataset['rendered'];
            // The picture stays until the new one is drawn (see `renderPage`); the text layer does not
            // fit a new scale, so it goes now.
            el.querySelectorAll('.abcp-pdf-page__render .textLayer').forEach((layer) => layer.remove());
        });
    }

    /**
     * Räumt pdf.js ab — beim Wechsel der Datei und in `ngOnDestroy`.
     *
     * ⚠️ **Darf NIE werfen.** Ein Wurf in `ngOnDestroy` bricht die Navigation des Routers ab, und die
     * nächste Seite bleibt leer. Genau so ist es passiert: `doc.destroy()` gibt es in pdf.js 6 nicht,
     * und wer die Einzelansicht verliess, landete auf einer leeren Seite. Jeder Schritt läuft deshalb
     * für sich geschützt — ein Aufräumfehler ist ein Logeintrag, kein kaputter Wechsel.
     */
    private teardown(): void {
        safely(() => this.observer?.disconnect());
        this.observer = null;
        safely(() => this.clearRendered());
        this.pdfPages.forEach((page) => safely(() => page.cleanup()));
        this.pdfPages.clear();
        if (this.task !== null) {
            destroyQuietly(this.task);
            this.task = null;
        }
    }

    // ── Markierungen ──────────────────────────────────────────────────────────────────────────

    private recomputeMarks(): void {
        const byPage = new Map<number, PageMarks[]>();
        for (const highlight of this.highlights ?? []) {
            const shell = this.pages.find((p) => p.number === highlight.page);
            const page = this.pdfPages.get(highlight.page);
            // Eine Seite ausserhalb des Dokuments: nicht zeichnen, nicht melden (ADR-0021 E5).
            if (!shell || !page) {
                continue;
            }
            const viewport = page.getViewport({ scale: this.scale });
            const boxes = highlight.rects.map((r) => toCssBox(viewport, shell.view, r));
            byPage.set(highlight.page, [...(byPage.get(highlight.page) ?? []), { id: highlight.id, author: highlight.author, boxes }]);
        }
        this.marksByPage = byPage;
        this.pendingBoxes = this.boxesOf(this.draft ?? this.pendingMark);
    }

    private boxesOf(mark: PdfAnnotationDraft | null): { page: number; boxes: CssBox[] } | null {
        const shell = mark === null ? undefined : this.pages.find((p) => p.number === mark.page);
        const page = mark === null ? undefined : this.pdfPages.get(mark.page);
        if (mark === null || !shell || !page) {
            return null;
        }
        const viewport = page.getViewport({ scale: this.scale });
        return { page: mark.page, boxes: mark.rects.map((r) => toCssBox(viewport, shell.view, r)) };
    }

    private setDraft(pageNumber: number, boxes: CssBox[], snippet: string | null): void {
        const page = this.pdfPages.get(pageNumber);
        const shell = this.pages.find((p) => p.number === pageNumber);
        if (!page || !shell || boxes.length === 0) {
            return;
        }
        const viewport = page.getViewport({ scale: this.scale });
        const rects = boxes.map((b) => toNormalisedRect(viewport, shell.view, b)).filter((r): r is NonNullable<typeof r> => r !== null);
        if (rects.length === 0) {
            return;
        }
        const last = boxes[boxes.length - 1];
        this.draft = {
            page: pageNumber,
            rects,
            snippet,
            actionLeft: Math.min(last.left + last.width, shell.baseWidth * this.scale - 8),
            actionTop: last.top + last.height + 4,
        };
        this.cdr.markForCheck();
    }

    // ── Navigation ────────────────────────────────────────────────────────────────────────────

    private goToPage(pageNumber: number): void {
        if (pageNumber < 1 || pageNumber > this.pages.length) {
            return;
        }
        const root = this.scroller?.nativeElement;
        const el = root?.querySelector<HTMLElement>(`.abcp-pdf-page[data-page="${pageNumber}"]`);
        if (root && el) {
            root.scrollTop = el.offsetTop - 16;
        }
        this.setCurrentPage(pageNumber);
    }

    /**
     * Die aktive Markierung in Sicht rollen. Läuft von selbst, wenn `activeHighlightId` sich ändert
     * oder der Beleg fertig geladen ist.
     *
     * ⚠️ Öffentlich für EINEN Fall (N4): der Host klickt das Zitat der Markierung, die schon aktiv
     * ist — die Eingabe ändert sich nicht, `ngOnChanges` läuft nicht, und wer inzwischen weggerollt
     * hat, sähe den Klick wortlos verpuffen. Zeichnet nichts, entscheidet nichts: nur rollen.
     */
    public revealActive(): void {
        const active = (this.highlights ?? []).find((h) => h.id === this.activeHighlightId);
        if (!active || this.status !== 'ready') {
            return;
        }
        const root = this.scroller?.nativeElement;
        const el = root?.querySelector<HTMLElement>(`.abcp-pdf-page[data-page="${active.page}"]`);
        const box = this.marksOf(active.page).find((m) => m.id === active.id)?.boxes[0];
        if (root && el) {
            root.scrollTop = el.offsetTop + (box ? Math.max(0, box.top - 80) : 0) - 16;
        }
        this.setCurrentPage(active.page);
    }

    private updateCurrentPage(): void {
        const root = this.scroller?.nativeElement;
        if (!root) {
            return;
        }
        const probe = root.scrollTop + root.clientHeight / 3;
        let current = 1;
        root.querySelectorAll<HTMLElement>('.abcp-pdf-page').forEach((el) => {
            if (el.offsetTop <= probe) {
                current = Number(el.dataset['page']);
            }
        });
        if (current !== this.currentPage) {
            this.zone.run(() => this.setCurrentPage(current));
        }
    }

    private setCurrentPage(pageNumber: number): void {
        if (pageNumber !== this.currentPage) {
            this.currentPage = pageNumber;
            this.pageChanged.emit(pageNumber);
            this.cdr.markForCheck();
        }
    }
}
